import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { dateTimeToMonDDYYYYWithTime, convertTo12HourFormat } from '../utils/dateUtils';
import LoadingSpinner from '../components/LoadingSpinner';

const WaiverForms = ({ userData }) => {
  const [hourWaivers, setHourWaivers] = useState([]);
  const [studentInfoMap, setStudentInfoMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [bulkQuery, setBulkQuery] = useState(''); // multiple names/emails, one per line
  const [debouncedQuery, setDebouncedQuery] = useState(''); // debounced version of bulkQuery
  const [sortBy, setSortBy] = useState('date'); // 'name', 'date', 'status', 'hours', 'createddate', 'supervisor'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc', 'desc'
  const [selectedWaiverIds, setSelectedWaiverIds] = useState(new Set());
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkAction, setBulkAction] = useState(null); // 'approve' | 'deny' | 'comment'
  const [bulkComment, setBulkComment] = useState('');
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkResult, setBulkResult] = useState(null); // { success, conflicts, errors }
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'Verified' | 'Pending' | 'Rejected' | 'Submitted'
  const [dateFilterField, setDateFilterField] = useState('createddate'); // 'createddate' | 'date'
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');

  useEffect(() => {
    fetchHourWaivers();
  }, [userData]);

  // Debounce the bulk query to prevent excessive filtering
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(bulkQuery);
    }, 300); // 300ms delay

    return () => clearTimeout(timer);
  }, [bulkQuery]);

  const fetchHourWaivers = async () => {
    setError('');
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Check if user has admin role
      const defaultAccessCard = userData && userData.accesscards && userData.default_accesscard_id
        ? userData.accesscards.find(ac => ac.accesscard_id === userData.default_accesscard_id)
        : null;
      
      const isAdmin = defaultAccessCard && defaultAccessCard.role && defaultAccessCard.role.includes('Admin');
      
      let waivers = [];
      
      if (isAdmin) {
        // Admin users can see all waivers
        const res = await axios.get('/api/hourwaivers/all', {
          headers: { Authorization: `Bearer ${token}` }
        });
        waivers = res.data.hourwaivers || [];
      } else {
        // Non-admin users can only see waivers where they are TOR or supervisor
        const res = await axios.get('/api/hourwaivers/all', {
          headers: { Authorization: `Bearer ${token}` }
        });
        const allWaivers = res.data.hourwaivers || [];
        
        // Filter waivers based on user's relationship
        waivers = allWaivers.filter(waiver => {
          // Check if user is the TOR
          if (waiver.tor && userData && userData.account_id === waiver.tor) {
            return true;
          }
          
          // Check if user is the supervisor
          if (waiver.supervisor_email && userData) {
            const userPrimaryEmail = userData.primary_email;
            let defaultCardEmail = null;
            if (userData.accesscards && userData.default_accesscard_id) {
              const defaultCard = userData.accesscards.find(ac => ac.accesscard_id === userData.default_accesscard_id);
              if (defaultCard) defaultCardEmail = defaultCard.email;
            }
            
            const isSupervisor = (
              (userPrimaryEmail && userPrimaryEmail.toLowerCase() === waiver.supervisor_email.toLowerCase()) ||
              (defaultCardEmail && defaultCardEmail.toLowerCase() === waiver.supervisor_email.toLowerCase())
            );
            
            if (isSupervisor) {
              return true;
            }
          }
          
          return false;
        });
      }
      
      setHourWaivers(waivers);

      // Fetch student info for all unique enrollments
      if (waivers.length > 0) {
        const uniqueEnrollmentIds = [...new Set(waivers.map(w => w.enrollment).filter(Boolean))];
        if (uniqueEnrollmentIds.length > 0) {
          try {
            const studentInfoRes = await axios.post('/api/enrollments/bulk-student-info',
              { enrollmentIds: uniqueEnrollmentIds },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            setStudentInfoMap(studentInfoRes.data.studentInfoMap || {});
          } catch (err) {
            console.error('Error fetching student info:', err);
            // Continue without student info if it fails
          }
        }
      }
    } catch (err) {
      console.error('Error fetching hour waivers:', err);
      setError('Failed to load hour waivers.');
    } finally {
      setLoading(false);
    }
  };

  const handleViewAsSchoolAdmin = (waiver) => {
    window.open(`/request-fe-hours-by-external-activity?command=schoolAdmin&id=${waiver.id}`, '_blank');
  };

  const handleViewAsSystemAdmin = (waiver) => {
    window.open(`/request-fe-hours-by-external-activity?command=systemAdmin&id=${waiver.id}`, '_blank');
  };

  // Helper function to get student display info
  const getStudentDisplay = (waiver) => {
    if (!waiver.enrollment || !studentInfoMap[waiver.enrollment]) {
      return 'Unknown Student';
    }

    const studentInfo = studentInfoMap[waiver.enrollment];
    const firstName = studentInfo.account?.first_name || '';
    const lastName = studentInfo.account?.last_name || '';
    const email = studentInfo.accesscard?.email || '';
    const gradeLevel = studentInfo.enrollment?.gradelevel || '';
    const schoolTitle = studentInfo.school?.title || '';
    const associationDisplay = studentInfo.association?.displayname || '';

    return {
      name: `${firstName} ${lastName}`.trim() || 'Unknown Name',
      email: email,
      gradeSchool: `${gradeLevel} grade at ${schoolTitle}${associationDisplay ? ` (${associationDisplay})` : ''}`.trim()
    };
  };

  // Derive display status from waiver - DB status may stay 'Submitted'; real state is in progress
  const getDerivedStatus = (waiver) => {
    if (waiver.status === 'Verified') return 'Verified';
    const progress = Array.isArray(waiver.progress) ? waiver.progress : (typeof waiver.progress === 'string' ? JSON.parse(waiver.progress || '[]') : []);
    const systemAdminStep = progress.find(s => s.decider === 'systemAdmin' || s.title === 'System Admin Approval');
    if (systemAdminStep?.status === 'complete') return 'Verified';
    if (systemAdminStep?.status === 'rejected') return 'Rejected';
    return 'Pending'; // In pipeline, awaiting system admin (or earlier steps)
  };

  // Helper function to truncate description
  const truncateDescription = (description, maxLength = 100) => {
    if (!description) return '';
    if (description.length <= maxLength) return description;
    return `${description.substring(0, maxLength)}...`;
  };

  // Filter waivers: search query -> date range -> status
  const filteredWaivers = useMemo(() => {
    // 1. Search filter
    const tokens = debouncedQuery.split(/\r?\n/).map(s => s.trim().toLowerCase()).filter(Boolean);
    let result = hourWaivers;
    if (tokens.length > 0) {
      result = result.filter(waiver => {
        const studentDisplay = getStudentDisplay(waiver);
        if (typeof studentDisplay === 'string') {
          const supervisorEmail = (waiver.supervisor_email || '').toLowerCase();
          return tokens.some(token => supervisorEmail.includes(token));
        }
        const studentName = studentDisplay.name.toLowerCase();
        const studentEmail = (studentDisplay.email || '').toLowerCase();
        const supervisorEmail = (waiver.supervisor_email || '').toLowerCase();
        return tokens.some(token => 
          studentName.includes(token) || 
          studentEmail.includes(token) || 
          supervisorEmail.includes(token)
        );
      });
    }
    // 2. Date range filter (empty = no limit)
    const field = dateFilterField === 'date' ? 'date' : 'createddate';
    if (dateStart) {
      const startMs = new Date(dateStart).setHours(0, 0, 0, 0);
      result = result.filter(w => {
        const d = w[field];
        return d && new Date(d).getTime() >= startMs;
      });
    }
    if (dateEnd) {
      const endMs = new Date(dateEnd).setHours(23, 59, 59, 999);
      result = result.filter(w => {
        const d = w[field];
        return d && new Date(d).getTime() <= endMs;
      });
    }
    // 3. Status filter (use derived status from progress)
    if (statusFilter !== 'all') {
      result = result.filter(w => getDerivedStatus(w) === statusFilter);
    }
    return result;
  }, [hourWaivers, debouncedQuery, studentInfoMap, statusFilter, dateFilterField, dateStart, dateEnd]);

  // Status counts for filter buttons (from waivers after search + date, before status filter)
  const statusCounts = useMemo(() => {
    const tokens = debouncedQuery.split(/\r?\n/).map(s => s.trim().toLowerCase()).filter(Boolean);
    let result = hourWaivers;
    if (tokens.length > 0) {
      result = result.filter(waiver => {
        const studentDisplay = getStudentDisplay(waiver);
        if (typeof studentDisplay === 'string') {
          const supervisorEmail = (waiver.supervisor_email || '').toLowerCase();
          return tokens.some(token => supervisorEmail.includes(token));
        }
        const studentName = studentDisplay.name.toLowerCase();
        const studentEmail = (studentDisplay.email || '').toLowerCase();
        const supervisorEmail = (waiver.supervisor_email || '').toLowerCase();
        return tokens.some(token => 
          studentName.includes(token) || 
          studentEmail.includes(token) || 
          supervisorEmail.includes(token)
        );
      });
    }
    const field = dateFilterField === 'date' ? 'date' : 'createddate';
    if (dateStart) {
      const startMs = new Date(dateStart).setHours(0, 0, 0, 0);
      result = result.filter(w => {
        const d = w[field];
        return d && new Date(d).getTime() >= startMs;
      });
    }
    if (dateEnd) {
      const endMs = new Date(dateEnd).setHours(23, 59, 59, 999);
      result = result.filter(w => {
        const d = w[field];
        return d && new Date(d).getTime() <= endMs;
      });
    }
    const counts = { Verified: 0, Pending: 0, Rejected: 0 };
    let total = 0;
    result.forEach(w => {
      const s = getDerivedStatus(w);
      counts[s] = (counts[s] || 0) + 1;
      total++;
    });
    return { ...counts, total };
  }, [hourWaivers, debouncedQuery, studentInfoMap, dateFilterField, dateStart, dateEnd]);

  // Sort waivers based on sortBy and sortOrder
  const sortWaivers = (waivers, sortBy, sortOrder) => {
    return [...waivers].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'name':
          const aStudentDisplay = getStudentDisplay(a);
          const bStudentDisplay = getStudentDisplay(b);
          aValue = typeof aStudentDisplay === 'string' ? aStudentDisplay : aStudentDisplay.name.toLowerCase();
          bValue = typeof bStudentDisplay === 'string' ? bStudentDisplay : bStudentDisplay.name.toLowerCase();
          break;
        case 'date':
          aValue = a.date ? new Date(a.date) : new Date(0);
          bValue = b.date ? new Date(b.date) : new Date(0);
          break;
        case 'status':
          // Order: Verified > Pending > Rejected (use derived status)
          const statusOrder = { 'Verified': 1, 'Pending': 2, 'Rejected': 3 };
          aValue = statusOrder[getDerivedStatus(a)] || 99;
          bValue = statusOrder[getDerivedStatus(b)] || 99;
          break;
        case 'hours':
          aValue = a.hours || 0;
          bValue = b.hours || 0;
          break;
        case 'createddate':
          aValue = a.createddate ? new Date(a.createddate) : new Date(0);
          bValue = b.createddate ? new Date(b.createddate) : new Date(0);
          break;
        case 'supervisor':
          aValue = (a.supervisor_email || '').toLowerCase();
          bValue = (b.supervisor_email || '').toLowerCase();
          break;
        default:
          return 0;
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });
  };

  // Apply sorting to filtered waivers
  const sortedWaivers = useMemo(() => {
    return sortWaivers(filteredWaivers, sortBy, sortOrder);
  }, [filteredWaivers, sortBy, sortOrder]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Verified':
        return '#388e3c'; // Green
      case 'Pending':
        return '#f57c00'; // Orange
      case 'Rejected':
        return '#d32f2f'; // Red
      default:
        return '#1976d2'; // Blue
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'Verified':
        return '✅ Verified';
      case 'Pending':
        return '⏰ Waiting for Approval';
      case 'Rejected':
        return '❌ Rejected';
      default:
        return status || 'Submitted';
    }
  };

  // Helper function to determine if current user can view as School Admin
  const canViewAsSchoolAdmin = (waiver) => {
    if (!userData || !userData.accesscards || !userData.default_accesscard_id) return false;
    const defaultCard = userData.accesscards.find(ac => ac.accesscard_id === userData.default_accesscard_id);
    if (!defaultCard) return false;
    if (defaultCard.role !== 'Admin') return false;
    if (defaultCard.global) return true;
    // Get student's school_id
    const studentInfo = waiver.enrollment && studentInfoMap[waiver.enrollment];
    const studentSchoolId = studentInfo && studentInfo.school && studentInfo.school.school_id;
    return defaultCard.school_id && studentSchoolId && defaultCard.school_id === studentSchoolId;
  };

  // Helper function to determine if current user can view as System Admin
  const canViewAsSystemAdmin = (waiver) => {
    if (!userData || !userData.accesscards || !userData.default_accesscard_id) return false;
    const defaultCard = userData.accesscards.find(ac => ac.accesscard_id === userData.default_accesscard_id);
    if (!defaultCard) return false;
    if (defaultCard.role == 'Super - Admin' || defaultCard.role == 'System Admin' || defaultCard.role == 'Super - System Admin') {
      if (defaultCard.global) return true;
      // Get student's school_id
      const studentInfo = waiver.enrollment && studentInfoMap[waiver.enrollment];
      const studentSchoolId = studentInfo && studentInfo.school && studentInfo.school.school_id;
      return defaultCard.school_id && studentSchoolId && defaultCard.school_id === studentSchoolId;
    }
  };

  // Super - Admin only: can perform bulk actions
  const isSuperAdmin = () => {
    if (!userData || !userData.accesscards || !userData.default_accesscard_id) return false;
    const defaultCard = userData.accesscards.find(ac => ac.accesscard_id === userData.default_accesscard_id);
    return defaultCard && defaultCard.role === 'Super - Admin';
  };

  const toggleWaiverSelection = (waiverId) => {
    setSelectedWaiverIds(prev => {
      const next = new Set(prev);
      if (next.has(waiverId)) next.delete(waiverId);
      else next.add(waiverId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedWaiverIds.size === sortedWaivers.length) {
      setSelectedWaiverIds(new Set());
    } else {
      setSelectedWaiverIds(new Set(sortedWaivers.map(w => w.id)));
    }
  };

  const openBulkModal = (action) => {
    setBulkAction(action);
    setBulkComment('');
    setBulkResult(null);
    setShowBulkModal(true);
  };

  const handleBulkSubmit = async () => {
    if (!bulkAction || selectedWaiverIds.size === 0) return;
    if (bulkAction === 'comment' && !bulkComment.trim()) return;
    setBulkSubmitting(true);
    setBulkResult(null);
    try {
      const token = localStorage.getItem('token');
      const waiverMap = Object.fromEntries(hourWaivers.map(w => [w.id, w]));
      const items = Array.from(selectedWaiverIds).map(id => ({
        id,
        expectedUpdatedDate: waiverMap[id]?.updateddate || null
      }));
      const who = userData ? `${userData.first_name || ''} ${userData.last_name || ''} (${userData.primary_email || ''})`.trim() : 'Super Admin';
      const payload = {
        items,
        action: bulkAction,
        who,
        ...(bulkComment.trim() ? { comment: bulkComment.trim() } : {})
      };
      const res = await axios.post('/api/hourwaivers/bulk-update', payload, {
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      });
      setBulkResult(res.data);
      if (res.data.conflicts && res.data.conflicts.length > 0) {
        // Refresh to get latest data when there are conflicts
        fetchHourWaivers();
      }
      if (res.data.success && res.data.success.length > 0) {
        fetchHourWaivers();
        setSelectedWaiverIds(new Set());
        if ((!res.data.conflicts || res.data.conflicts.length === 0) && (!res.data.errors || res.data.errors.length === 0)) {
          setShowBulkModal(false);
        }
      }
    } catch (err) {
      setBulkResult({ success: [], conflicts: [], errors: [{ error: err.response?.data?.error || err.message || 'Request failed' }] });
    } finally {
      setBulkSubmitting(false);
    }
  };

  const closeBulkModal = () => {
    setShowBulkModal(false);
    setBulkAction(null);
    setBulkComment('');
    setBulkResult(null);
  };

  // Helper function to determine if current user can view as Supervisor
  const canViewAsSupervisor = (waiver) => {
    if (!userData) return false;
    const supervisorEmail = waiver.supervisor_email;
    if (!supervisorEmail) return false;
    const userPrimaryEmail = userData.primary_email;
    let defaultCardEmail = null;
    if (userData.accesscards && userData.default_accesscard_id) {
      const defaultCard = userData.accesscards.find(ac => ac.accesscard_id === userData.default_accesscard_id);
      if (defaultCard) defaultCardEmail = defaultCard.email;
    }
    return (
      (userPrimaryEmail && userPrimaryEmail.toLowerCase() === supervisorEmail.toLowerCase()) ||
      (defaultCardEmail && defaultCardEmail.toLowerCase() === supervisorEmail.toLowerCase())
    );
  };

  // Helper function to determine if current user can view as TOR
  const canViewAsTOR = (waiver) => {
    if (!userData || !userData.account_id) return false;
    return waiver.tor && userData.account_id === waiver.tor;
  };

  const handleViewAsSupervisor = (waiver) => {
    window.open(`/request-fe-hours-by-external-activity?command=supervisor&id=${waiver.id}`, '_blank');
  };

  const handleViewAsTOR = (waiver) => {
    window.open(`/request-fe-hours-by-external-activity?command=tor&id=${waiver.id}`, '_blank');
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <h1 style={{ marginBottom: '24px', color: '#333', fontSize: 28 }}>Waiver Forms</h1>
      
      {/* Search filter */}
      <div style={{ 
        marginBottom: '24px',
        padding: '16px',
        backgroundColor: '#f8fafc',
        borderRadius: '8px',
        border: '1px solid #e2e8f0'
      }}>
        <label style={{ 
          display: 'block', 
          marginBottom: '8px', 
          fontSize: '14px', 
          fontWeight: 500,
          color: '#374151'
        }}>
          Filter by name or email (one per line):
        </label>
        <textarea
          value={bulkQuery}
          onChange={(e) => setBulkQuery(e.target.value)}
          placeholder="Type names or emails to filter live (one per line)"
          rows={3}
          style={{ 
            padding: '8px 12px', 
            borderRadius: 8, 
            border: '1px solid #e5e7eb', 
            width: '100%',
            fontSize: '14px',
            fontFamily: 'inherit',
            resize: 'vertical'
          }}
        />
      </div>

      {/* Status filter buttons */}
      {!loading && !error && hourWaivers.length > 0 && (
        <div style={{
          marginBottom: '24px',
          padding: '16px',
          backgroundColor: '#f8fafc',
          borderRadius: '8px',
          border: '1px solid #e2e8f0'
        }}>
          <label style={{
            display: 'block',
            marginBottom: '10px',
            fontSize: '14px',
            fontWeight: 500,
            color: '#374151'
          }}>
            Status
          </label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
            {[
              { value: 'all', label: 'All', count: statusCounts.total },
              { value: 'Verified', label: 'Verified', count: statusCounts.Verified },
              { value: 'Pending', label: 'Waiting for Approval', count: statusCounts.Pending },
              { value: 'Rejected', label: 'Rejected', count: statusCounts.Rejected }
            ].map(({ value, label, count }) => (
              <label
                key={value}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 14px',
                  borderRadius: '8px',
                  border: `2px solid ${statusFilter === value ? '#3b82f6' : '#e2e8f0'}`,
                  backgroundColor: statusFilter === value ? '#eff6ff' : '#fff',
                  cursor: 'pointer',
                  fontWeight: statusFilter === value ? 600 : 500,
                  fontSize: '14px',
                  transition: 'all 0.15s ease'
                }}
              >
                <input
                  type="radio"
                  name="statusFilter"
                  value={value}
                  checked={statusFilter === value}
                  onChange={() => setStatusFilter(value)}
                  style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#3b82f6' }}
                />
                {label} ({count})
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Date range filter */}
      {!loading && !error && hourWaivers.length > 0 && (
        <div style={{
          marginBottom: '24px',
          padding: '16px',
          backgroundColor: '#f8fafc',
          borderRadius: '8px',
          border: '1px solid #e2e8f0'
        }}>
          <label style={{
            display: 'block',
            marginBottom: '10px',
            fontSize: '14px',
            fontWeight: 500,
            color: '#374151'
          }}>
            Date range
          </label>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px' }}>
            <select
              value={dateFilterField}
              onChange={(e) => setDateFilterField(e.target.value)}
              style={{
                background: '#ffffff',
                color: '#374151',
                border: '1px solid #d1d5db',
                padding: '8px 12px',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                minWidth: '160px'
              }}
            >
              <option value="createddate">Created Date</option>
              <option value="date">Activity Date</option>
            </select>
            <input
              type="date"
              value={dateStart}
              onChange={(e) => setDateStart(e.target.value)}
              placeholder="Start"
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                fontSize: '14px',
                minWidth: '140px'
              }}
            />
            <span style={{ color: '#64748b', fontSize: '14px' }}>to</span>
            <input
              type="date"
              value={dateEnd}
              onChange={(e) => setDateEnd(e.target.value)}
              placeholder="End"
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                fontSize: '14px',
                minWidth: '140px'
              }}
            />
            {(dateStart || dateEnd) && (
              <button
                onClick={() => { setDateStart(''); setDateEnd(''); }}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  background: '#fff',
                  fontSize: '14px',
                  cursor: 'pointer',
                  color: '#64748b'
                }}
              >
                Clear dates
              </button>
            )}
          </div>
          <div style={{ marginTop: '6px', fontSize: '12px', color: '#64748b' }}>
            Leave dates empty for no limit (beginning to end of time)
          </div>
        </div>
      )}

      {/* Sort controls */}
      {!loading && !error && hourWaivers.length > 0 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '24px',
          padding: '8px 12px',
          backgroundColor: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: '8px'
        }}>
          <label style={{
            fontSize: '14px',
            fontWeight: '500',
            color: '#374151',
            whiteSpace: 'nowrap'
          }}>
            Sort by:
          </label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{
              background: '#ffffff',
              color: '#374151',
              border: '1px solid #d1d5db',
              padding: '6px 8px',
              borderRadius: '4px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              minWidth: '140px'
            }}
          >
            <option value="date">Date</option>
            <option value="status">Status</option>
            <option value="name">Student Name</option>
            <option value="hours">Hours</option>
            <option value="createddate">Created Date</option>
            <option value="supervisor">Supervisor Email</option>
          </select>
          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
            style={{
              background: '#ffffff',
              color: '#374151',
              border: '1px solid #d1d5db',
              padding: '6px 10px',
              borderRadius: '4px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              minWidth: '36px',
              height: '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#f9fafb';
              e.target.style.borderColor = '#9ca3af';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#ffffff';
              e.target.style.borderColor = '#d1d5db';
            }}
          >
            {sortOrder === 'asc' ? '↑' : '↓'}
          </button>
        </div>
      )}

      {/* Bulk action bar - Super Admin only */}
      {isSuperAdmin() && selectedWaiverIds.size > 0 && !loading && !error && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '24px',
          padding: '12px 16px',
          backgroundColor: '#eff6ff',
          border: '1px solid #3b82f6',
          borderRadius: '8px'
        }}>
          <span style={{ fontWeight: 600, color: '#1e40af' }}>
            {selectedWaiverIds.size} waiver{selectedWaiverIds.size !== 1 ? 's' : ''} selected
          </span>
          <button
            onClick={() => openBulkModal('approve')}
            style={{
              padding: '8px 16px',
              borderRadius: 6,
              background: '#22c55e',
              color: 'white',
              border: 'none',
              fontWeight: 600,
              cursor: 'pointer',
              fontSize: 14
            }}
          >
            Approve
          </button>
          <button
            onClick={() => openBulkModal('deny')}
            style={{
              padding: '8px 16px',
              borderRadius: 6,
              background: '#ef4444',
              color: 'white',
              border: 'none',
              fontWeight: 600,
              cursor: 'pointer',
              fontSize: 14
            }}
          >
            Deny
          </button>
          <button
            onClick={() => openBulkModal('comment')}
            style={{
              padding: '8px 16px',
              borderRadius: 6,
              background: '#6366f1',
              color: 'white',
              border: 'none',
              fontWeight: 600,
              cursor: 'pointer',
              fontSize: 14
            }}
          >
            Add Comment
          </button>
          <button
            onClick={() => setSelectedWaiverIds(new Set())}
            style={{
              padding: '8px 16px',
              borderRadius: 6,
              background: '#64748b',
              color: 'white',
              border: 'none',
              fontWeight: 600,
              cursor: 'pointer',
              fontSize: 14
            }}
          >
            Clear selection
          </button>
        </div>
      )}
      
      {/* Show filtering message for non-admin users */}
      {userData && userData.accesscards && userData.default_accesscard_id && (() => {
        const defaultAccessCard = userData.accesscards.find(ac => ac.accesscard_id === userData.default_accesscard_id);
        const isAdmin = defaultAccessCard && defaultAccessCard.role && defaultAccessCard.role.includes('Admin');
        
        if (!isAdmin) {
          return (
            <div style={{
              background: '#e3f2fd',
              border: '1px solid #2196f3',
              borderRadius: 8,
              padding: '12px 16px',
              marginBottom: '24px',
              color: '#1976d2',
              fontSize: '14px',
              fontWeight: 500
            }}>
              <strong>Note:</strong> You are viewing only waiver forms where you are the Teacher of Record (TOR) or Supervisor. 
              Admin users can view all waiver forms.
            </div>
          );
        }
        return null;
      })()}

      {loading && <LoadingSpinner />}

      {error && (
        <div style={{
          color: '#d32f2f',
          background: '#ffebee',
          border: '1px solid #ffcdd2',
          borderRadius: 8,
          padding: '1rem',
          marginBottom: '1rem'
        }}>
          {error}
        </div>
      )}

      {!loading && !error && sortedWaivers.length === 0 && (
        <div style={{
          color: '#888',
          textAlign: 'center',
          margin: '2rem 0',
          fontSize: '1.1rem'
        }}>
          {hourWaivers.length === 0 
            ? 'No waiver forms found.' 
            : 'No waiver forms match your search criteria.'}
        </div>
      )}

      {!loading && !error && sortedWaivers.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {isSuperAdmin() && (
            <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>
                <input
                  type="checkbox"
                  checked={selectedWaiverIds.size === sortedWaivers.length && sortedWaivers.length > 0}
                  onChange={toggleSelectAll}
                  style={{ width: 18, height: 18, cursor: 'pointer' }}
                />
                Select all ({sortedWaivers.length})
              </label>
            </div>
          )}
          {sortedWaivers.map(waiver => {
            const studentDisplay = getStudentDisplay(waiver);
            return (
              <div
                key={waiver.id}
                style={{
                  border: selectedWaiverIds.has(waiver.id) ? '2px solid #3b82f6' : '2px solid #e0e0e0',
                  borderRadius: 8,
                  padding: '1.5rem',
                  background: selectedWaiverIds.has(waiver.id) ? '#eff6ff' : '#fff',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  transition: 'box-shadow 0.2s'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                  {isSuperAdmin() && (
                    <div style={{ marginRight: '12px', flexShrink: 0 }}>
                      <input
                        type="checkbox"
                        checked={selectedWaiverIds.has(waiver.id)}
                        onChange={() => toggleWaiverSelection(waiver.id)}
                        style={{ width: 18, height: 18, cursor: 'pointer' }}
                        title="Select for bulk action"
                      />
                    </div>
                  )}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: '1.1rem', color: '#1976d2', marginBottom: '0.5rem' }}>
                      External Activity Waiver
                    </div>
                    <div style={{ color: '#555', fontSize: '0.95rem', marginBottom: '0.5rem' }}>
                      {waiver.date ? dateTimeToMonDDYYYYWithTime(waiver.date, false) : 'No date'}
                      {waiver.timestart && waiver.timeend
                        ? ` | ${convertTo12HourFormat(waiver.timestart)} - ${convertTo12HourFormat(waiver.timeend)}`
                        : ''}
                    </div>
                    {waiver.description && (
                      <div style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                        {truncateDescription(waiver.description, 100)}
                      </div>
                    )}
                    {waiver.hours && (
                      <div style={{ color: '#555', fontSize: '0.9rem', fontWeight: 500 }}>
                        Hours: {waiver.hours}
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.5rem' }}>
                    <span style={{
                      color: getStatusColor(getDerivedStatus(waiver)),
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      background: `${getStatusColor(getDerivedStatus(waiver))}20`
                    }}>
                      {getStatusText(getDerivedStatus(waiver))}
                    </span>

                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      {canViewAsSchoolAdmin(waiver) && (
                        <button
                          style={{ 
                            padding: '6px 12px', 
                            borderRadius: 4, 
                            background: '#1976d2', 
                            color: 'white', 
                            border: 'none', 
                            cursor: 'pointer',
                            fontWeight: 600,
                            fontSize: 12,
                            transition: 'background 0.2s'
                          }}
                          onClick={() => handleViewAsSchoolAdmin(waiver)}
                          title="View as School Admin"
                        >
                          School Admin
                        </button>
                      )}
                      {canViewAsSystemAdmin(waiver) && (
                        <button
                          style={{ 
                            padding: '6px 12px', 
                            borderRadius: 4, 
                            background: '#4caf50', 
                            color: 'white', 
                            border: 'none', 
                            cursor: 'pointer',
                            fontWeight: 600,
                            fontSize: 12,
                            transition: 'background 0.2s'
                          }}
                          onClick={() => handleViewAsSystemAdmin(waiver)}
                          title="View as System Admin"
                        >
                          System Admin
                        </button>
                      )}
                      {canViewAsSupervisor(waiver) && (
                        <button
                          style={{
                            padding: '6px 12px',
                            borderRadius: 4,
                            background: '#a21caf',
                            color: 'white',
                            border: 'none',
                            cursor: 'pointer',
                            fontWeight: 600,
                            fontSize: 12,
                            transition: 'background 0.2s'
                          }}
                          onClick={() => handleViewAsSupervisor(waiver)}
                          title="View as Supervisor"
                        >
                          Supervisor
                        </button>
                      )}
                      {canViewAsTOR(waiver) && (
                        <button
                          style={{
                            padding: '6px 12px',
                            borderRadius: 4,
                            background: '#f59e0b',
                            color: 'white',
                            border: 'none',
                            cursor: 'pointer',
                            fontWeight: 600,
                            fontSize: 12,
                            transition: 'background 0.2s'
                          }}
                          onClick={() => handleViewAsTOR(waiver)}
                          title="View as Teacher of Record"
                        >
                          TOR
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Student Information */}
                <div style={{
                  display: 'flex',
                  gap: '2rem',
                  fontSize: '0.85rem',
                  color: '#666',
                  borderTop: '1px solid #e0e0e0',
                  paddingTop: '1rem',
                  marginBottom: '1rem'
                }}>
                  <div>
                    <strong>Student:</strong> {studentDisplay.name} ({studentDisplay.email})
                  </div>
                  <div>
                    <strong>Grade/School:</strong> {studentDisplay.gradeSchool}
                  </div>
                </div>

                {/* Additional details */}
                <div style={{
                  display: 'flex',
                  gap: '2rem',
                  fontSize: '0.85rem',
                  color: '#666',
                  borderTop: '1px solid #e0e0e0',
                  paddingTop: '1rem'
                }}>
                  <div>
                    <strong>Type:</strong> {waiver.type || 'Field Experience'}
                  </div>
                  <div>
                    <strong>Created:</strong> {waiver.createddate ? dateTimeToMonDDYYYYWithTime(waiver.createddate, false) : 'N/A'}
                  </div>
                  {waiver.supervisor_email && (
                    <div>
                      <strong>Supervisor:</strong> {waiver.supervisor_email}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Bulk action modal */}
      {showBulkModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 12,
            padding: 24,
            maxWidth: 480,
            width: '100%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: 20, fontWeight: 600 }}>
              Bulk {bulkAction === 'approve' ? 'Approve' : bulkAction === 'deny' ? 'Deny' : 'Add Comment'}
            </h3>
            <p style={{ color: '#64748b', fontSize: 14, marginBottom: 16 }}>
              Apply to {selectedWaiverIds.size} selected waiver{selectedWaiverIds.size !== 1 ? 's' : ''}. Version checks will run to ensure no items were updated elsewhere.
            </p>
            {(bulkAction === 'approve' || bulkAction === 'deny') && (
              <p style={{ fontSize: 13, color: '#64748b', marginBottom: 12 }}>
                Optional: add a comment to attach to each waiver.
              </p>
            )}
            <textarea
              value={bulkComment}
              onChange={(e) => setBulkComment(e.target.value)}
              placeholder={bulkAction === 'comment' ? 'Enter comment (required)' : 'Optional comment...'}
              rows={4}
              style={{
                width: '100%',
                padding: 12,
                borderRadius: 8,
                border: '1px solid #e2e8f0',
                fontSize: 14,
                fontFamily: 'inherit',
                resize: 'vertical',
                marginBottom: 16
              }}
            />
            {bulkResult && (
              <div style={{
                marginBottom: 16,
                padding: 12,
                borderRadius: 8,
                backgroundColor: bulkResult.conflicts?.length > 0 || bulkResult.errors?.length > 0 ? '#fef2f2' : '#f0fdf4',
                border: `1px solid ${bulkResult.conflicts?.length > 0 || bulkResult.errors?.length > 0 ? '#fecaca' : '#bbf7d0'}`,
                fontSize: 14
              }}>
                {bulkResult.success?.length > 0 && (
                  <div style={{ color: '#166534', marginBottom: 4 }}>
                    ✓ {bulkResult.success.length} updated successfully
                  </div>
                )}
                {bulkResult.conflicts?.length > 0 && (
                  <div style={{ color: '#b91c1c' }}>
                    ⚠ {bulkResult.conflicts.length} had version conflicts (updated elsewhere). Refresh to see latest.
                  </div>
                )}
                {bulkResult.errors?.length > 0 && (
                  <div style={{ color: '#b91c1c' }}>
                    ✗ {bulkResult.errors.length} failed: {bulkResult.errors[0]?.error || 'Unknown error'}
                  </div>
                )}
              </div>
            )}
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                onClick={closeBulkModal}
                style={{
                  padding: '10px 20px',
                  borderRadius: 8,
                  border: '1px solid #d1d5db',
                  background: '#fff',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: 14
                }}
              >
                {bulkResult?.success?.length > 0 ? 'Close' : 'Cancel'}
              </button>
              <button
                onClick={handleBulkSubmit}
                disabled={bulkSubmitting || (bulkAction === 'comment' && !bulkComment.trim())}
                style={{
                  padding: '10px 20px',
                  borderRadius: 8,
                  border: 'none',
                  background: bulkAction === 'approve' ? '#22c55e' : bulkAction === 'deny' ? '#ef4444' : '#6366f1',
                  color: 'white',
                  fontWeight: 600,
                  cursor: bulkSubmitting ? 'not-allowed' : 'pointer',
                  fontSize: 14,
                  opacity: (bulkSubmitting || (bulkAction === 'comment' && !bulkComment.trim())) ? 0.6 : 1
                }}
              >
                {bulkSubmitting ? 'Processing...' : bulkAction === 'approve' ? 'Approve' : bulkAction === 'deny' ? 'Deny' : 'Add Comment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WaiverForms; 